import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, getDoc, doc, addDoc } from 'firebase/firestore';
import './PlaylistSaveDialog.css';

function PlaylistSaveDialog({ trackCount, onSave, onCancel, user }) {
  console.log('🎬 PlaylistSaveDialog component rendered');
  console.log(
    '🎬 Props received - trackCount:',
    trackCount,
    'onSave:',
    !!onSave,
    'onCancel:',
    !!onCancel,
    'user:',
    !!user
  );

  const [playlistName, setPlaylistName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [availablePlaylists, setAvailablePlaylists] = useState([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState('');
  const [isAppending, setIsAppending] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Load members when switching to append mode
  useEffect(() => {
    if (user && isAppending) {
      loadMembers();
    } else {
      setMembers([]);
      setSelectedMemberId('');
      setAvailablePlaylists([]);
    }
  }, [user, isAppending]);

  // Load playlists when a member is selected
  useEffect(() => {
    if (user && isAppending && selectedMemberId) {
      loadAvailablePlaylists();
    } else if (isAppending && !selectedMemberId) {
      setAvailablePlaylists([]);
    }
  }, [user, isAppending, selectedMemberId]);

  // Load members (current user + other users with public playlists)
  const loadMembers = async () => {
    if (!user) return;
    
    setLoadingMembers(true);
    try {
      const membersList = [];
      
      // Add current user first
      try {
        const currentUserDoc = await getDoc(doc(db, 'users', user.uid));
        const currentUserData = currentUserDoc.exists() ? currentUserDoc.data() : {};
        membersList.push({
          id: user.uid,
          email: user.email || '',
          displayName: currentUserData.displayName || user.email || 'You',
          photoURL: currentUserData.photoURL || null,
        });
      } catch (err) {
        // Fallback if user doc doesn't exist
        membersList.push({
          id: user.uid,
          email: user.email || '',
          displayName: user.email || 'You',
          photoURL: null,
        });
      }

      // Query for all public playlists to find other members
      const playlistsQuery = query(
        collection(db, 'playlists'),
        where('isPublic', '==', true)
      );
      const playlistsSnapshot = await getDocs(playlistsQuery);

      // Get unique user IDs from public playlists (excluding current user)
      const userIds = new Set();
      playlistsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.userId && data.isPublic === true && data.userId !== user.uid) {
          userIds.add(data.userId);
        }
      });

      // Fetch user documents for each unique user ID
      for (const userId of userIds) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            membersList.push({
              id: userId,
              email: userData.email || '',
              displayName: userData.displayName || userData.email || 'Unknown User',
              photoURL: userData.photoURL || null,
            });
          } else {
            // Fallback: Use email from playlist data
            const userPublicPlaylists = playlistsSnapshot.docs.filter(
              (doc) => doc.data().userId === userId
            );
            if (userPublicPlaylists.length > 0) {
              const firstPlaylist = userPublicPlaylists[0].data();
              membersList.push({
                id: userId,
                email: firstPlaylist.userEmail || '',
                displayName: firstPlaylist.userEmail || 'Unknown User',
                photoURL: null,
              });
            }
          }
        } catch (err) {
          console.error('Error loading user:', userId, err);
        }
      }

      setMembers(membersList);
      
      // Auto-select current user by default
      if (membersList.length > 0) {
        setSelectedMemberId(user.uid);
      }
    } catch (error) {
      console.error('❌ Error loading members:', error);
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Load available playlists for selected member
  const loadAvailablePlaylists = async () => {
    if (!user || !selectedMemberId) return;
    
    try {
      // If selected member is current user, show all playlists (including private)
      // Otherwise, only show public playlists
      let q;
      if (selectedMemberId === user.uid) {
        q = query(
          collection(db, 'playlists'),
          where('userId', '==', selectedMemberId)
        );
      } else {
        q = query(
          collection(db, 'playlists'),
          where('userId', '==', selectedMemberId),
          where('isPublic', '==', true)
        );
      }
      
      const querySnapshot = await getDocs(q);
      const playlists = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Include all playlists, including empty ones and Road Trip playlists
        playlists.push({ 
          id: doc.id, 
          name: data.name || 'Unnamed Playlist',
          trackCount: data.trackCount || 0,
          source: data.source || 'local',
          tracks: data.tracks || [],
          ...data 
        });
      });

      // Sort by updatedAt or createdAt (most recent first)
      playlists.sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bTime - aTime;
      });

      setAvailablePlaylists(playlists);
    } catch (error) {
      console.error('❌ Error loading playlists:', error);
      setAvailablePlaylists([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('💾 PlaylistSaveDialog handleSubmit called');
    console.log('💾 isAppending:', isAppending);
    console.log('💾 selectedPlaylistId:', selectedPlaylistId);
    console.log('💾 playlistName:', playlistName);
    console.log('💾 description:', description);
    
    if (isAppending && selectedPlaylistId) {
      // Append mode - enable live saving
      console.log('💾 Calling onSave callback with append data for live save');
      if (onSave) {
        onSave({
          appendToPlaylistId: selectedPlaylistId,
          isAppending: true,
          isLiveSave: true, // Enable live saving for append mode
        });
        console.log('💾 onSave callback executed - live save enabled');
      } else {
        console.error('❌ onSave callback is not defined!');
      }
    } else if (!isAppending && playlistName.trim()) {
      // Create new mode - create playlist immediately
      console.log('💾 Creating playlist immediately:', playlistName.trim());
      try {
        const playlistDoc = {
          name: playlistName.trim(),
          description: description.trim(),
          userId: user.uid,
          userEmail: user.email,
          tracks: [],
          trackCount: 0,
          isPublic: isPublic,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const docRef = await addDoc(collection(db, 'playlists'), playlistDoc);
        console.log('✅ Playlist created with ID:', docRef.id);
        
        // Call onSave with the playlist ID for real-time saving
        if (onSave) {
          onSave({
            playlistId: docRef.id,
            name: playlistName.trim(),
            description: description.trim(),
            isPublic: isPublic,
            isAppending: false,
            isLiveSave: true, // Flag to indicate real-time saving
          });
          console.log('💾 onSave callback executed with playlist ID');
        } else {
          console.error('❌ onSave callback is not defined!');
        }
      } catch (error) {
        console.error('❌ Error creating playlist:', error);
        alert(`Failed to create playlist: ${error.message}`);
      }
    } else {
      console.warn('⚠️ Invalid form state - not saving');
    }
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay itself, not the dialog
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  console.log('🎬 PlaylistSaveDialog render - returning JSX');

  return (
    <div
      className="playlist-save-dialog-overlay"
      onClick={(e) => {
        console.log('🎬 Overlay clicked');
        handleOverlayClick(e);
      }}
      onMouseDown={(e) => {
        console.log('🎬 Overlay mouseDown');
        e.stopPropagation();
      }}
      onTouchStart={(e) => {
        console.log('🎬 Overlay touchStart');
        e.stopPropagation();
      }}
      style={{
        pointerEvents: 'auto',
        zIndex: 10000,
      }}
    >
      <div
        className="playlist-save-dialog"
        onClick={(e) => {
          console.log('🎬 Dialog clicked');
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          console.log('🎬 Dialog mouseDown');
          e.stopPropagation();
        }}
        onTouchStart={(e) => {
          console.log('🎬 Dialog touchStart');
          e.stopPropagation();
        }}
        style={{
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 10001,
        }}
      >
        <h2>💾 Save Playlist</h2>
        <p className="playlist-track-count">
          {trackCount} song{trackCount !== 1 ? 's' : ''} selected
        </p>

        <form onSubmit={handleSubmit}>
          {/* Toggle between Create New and Append to Existing */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={() => {
                setIsAppending(false);
                setSelectedPlaylistId('');
              }}
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '16px',
                backgroundColor: !isAppending ? '#667eea' : '#ccc',
                color: !isAppending ? 'white' : 'black',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Create New
            </button>
            <button
              type="button"
              onClick={() => {
                setIsAppending(true);
                setPlaylistName('');
                setDescription('');
                setIsPublic(false);
                setSelectedPlaylistId('');
                setSelectedMemberId('');
                // Members will be loaded via useEffect when isAppending becomes true
              }}
              style={{
                flex: 1,
                padding: '10px',
                fontSize: '16px',
                backgroundColor: isAppending ? '#667eea' : '#ccc',
                color: isAppending ? 'white' : 'black',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Append to Existing
            </button>
          </div>

          {isAppending ? (
            <>
              {/* Step 1: Select Member */}
              <div className="form-group">
                <label>Select Member *</label>
                <select
                  value={selectedMemberId}
                  onChange={(e) => {
                    setSelectedMemberId(e.target.value);
                    setSelectedPlaylistId(''); // Reset playlist selection when member changes
                  }}
                  className="playlist-input"
                  required
                  disabled={loadingMembers}
                  style={{ 
                    width: '100%', 
                    padding: '10px', 
                    fontSize: '16px',
                    backgroundColor: '#2a2a2a',
                    color: '#fff',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    opacity: loadingMembers ? 0.6 : 1
                  }}
                >
                  <option value="" style={{ backgroundColor: '#2a2a2a', color: '#fff' }}>
                    {loadingMembers ? 'Loading members...' : '-- Select a member --'}
                  </option>
                  {members.map((member) => (
                    <option 
                      key={member.id} 
                      value={member.id}
                      style={{ backgroundColor: '#2a2a2a', color: '#fff' }}
                    >
                      {member.id === user.uid ? '👤 ' : ''}{member.displayName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Step 2: Select Playlist (only shown after member is selected) */}
              {selectedMemberId && (
                <>
                  <div className="form-group">
                    <label>Select Playlist *</label>
                    <select
                      value={selectedPlaylistId}
                      onChange={(e) => setSelectedPlaylistId(e.target.value)}
                      className="playlist-input"
                      required
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        fontSize: '16px',
                        backgroundColor: '#2a2a2a',
                        color: '#fff',
                        border: '1px solid #555',
                        borderRadius: '4px'
                      }}
                    >
                      <option value="" style={{ backgroundColor: '#2a2a2a', color: '#fff' }}>-- Select a playlist --</option>
                      {availablePlaylists.map((playlist) => (
                        <option 
                          key={playlist.id} 
                          value={playlist.id}
                          style={{ backgroundColor: '#2a2a2a', color: '#fff' }}
                        >
                          {playlist.name} ({playlist.trackCount || 0} songs)
                          {playlist.source === 'roadTrip' ? ' 🚗' : ''}
                          {playlist.isPublic ? ' 🌐' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {availablePlaylists.length === 0 && (
                    <p style={{ marginBottom: '15px', color: '#999', fontSize: '14px', fontStyle: 'italic' }}>
                      {selectedMemberId === user.uid 
                        ? 'No playlists found. Create a playlist first.' 
                        : 'No public playlists available for this member.'}
                    </p>
                  )}
                  {availablePlaylists.length > 0 && (
                    <p style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
                      Add {trackCount} song{trackCount !== 1 ? 's' : ''} to the selected playlist.
                      Duplicate songs will be skipped.
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Playlist Name *</label>
                <input
                  type="text"
                  className="playlist-input"
                  placeholder="e.g., My Favorite Rock Songs"
                  value={playlistName}
                  onChange={(e) => setPlaylistName(e.target.value)}
                  autoFocus
                  required
                  maxLength={50}
                />
              </div>

              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  className="playlist-textarea"
                  placeholder="Add a description for this playlist..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={200}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Make Public (other users can discover it)
                </label>
              </div>
            </>
          )}

          <div className="dialog-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={(e) => {
                console.log('❌ Cancel button clicked');
                e.preventDefault();
                e.stopPropagation();
                if (onCancel) {
                  onCancel();
                } else {
                  console.error('❌ onCancel callback is not defined!');
                }
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={isAppending ? (!selectedMemberId || !selectedPlaylistId) : !playlistName.trim()}
              onClick={(e) => {
                console.log('💾 Save Playlist button clicked in dialog');
                console.log('💾 Button disabled?', isAppending ? (!selectedMemberId || !selectedPlaylistId) : !playlistName.trim());
                // Let the form handle submit, but log
              }}
            >
              💾 {isAppending ? 'Append to Playlist' : 'Save Playlist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PlaylistSaveDialog;
